import {
  createContext, useMemo, useEffect, useState, useCallback,
} from 'react';
import api from '../services/api';
import placeholder from '../assets/img/img_placeholder_20230927.jpg';
import { ImageType } from '../utils/image';
import { PaginationMetaLink } from '../models/Paginator';

export interface ImageItem {
  id: number,
  url: string,
  thumbnail: string
}

interface ImagesContextProps {
  apiError: string | null,
  validationError: string | null,
  placeHolderImage?: string,
  Create: (data: ArrayBuffer, type: ImageType) => Promise<{ id?: number, url?: string }>,
  GetImages: (type: ImageType, page?: number, perPage?: number) => Promise<ImageItem[]>,
  images: ImageItem[]
  total: number
  links?: PaginationMetaLink[]
  currentPage?: number
  toItem?: number
  fromItem?: number
  reset: () => void
  destroyImage: (id: number, cb?: () => void) => void
  exchangeForUrl: (url: string, defaultUrl?: string) => Promise<any>
}

export const ImagesContext = createContext<ImagesContextProps>({
  Create: (): Promise<{ id: number, url: string }> => Promise.resolve({ id: 0, url: '' }),
  GetImages: (): Promise<ImageItem[]> => Promise.resolve([]),
  images: [],
  total: 0,
  destroyImage: () => { },
  exchangeForUrl: null,
  reset: null,
  apiError: null,
  validationError: null,
});

interface ImagesProviderProps {
  children: React.ReactNode
}

const ImagesProvider: React.FC<ImagesProviderProps> = (props: ImagesProviderProps) => {
  const { children } = props;
  const [placeHolderImage, setPlaceHolderImage] = useState('');
  const [images, setImages] = useState<ImageItem[]>([]);
  const [total, setTotal] = useState(0);
  const [links, setLinks] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [toItem, setToItem] = useState(0);
  const [fromItem, setFromItem] = useState(0);
  const [apiError, setApiError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // convert placeholder in static folder into base64 for exporting html
  useEffect(() => {
    const xhr = new XMLHttpRequest();
    xhr.onload = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result !== 'string') {
          return;
        }
        setPlaceHolderImage(reader.result);
      };
      reader.readAsDataURL(xhr.response);
    };
    xhr.open('GET', placeholder);
    xhr.responseType = 'blob';
    xhr.send();
  }, []);

  const Create = useCallback(async (data: ArrayBuffer, type: ImageType): Promise<{ id?: number, url?: string, thumbnail?: string }> => {
    setApiError(null);
    setValidationError(null);

    if (!navigator.onLine) {
      setApiError('インターネット接続がありません。接続を確認してください。');
      return {};
    }

    const blobData = new Blob([data]);
    const formData = new FormData();
    formData.append('file', blobData);
    formData.append('type', type);

    try {
      const res = await api.post('/v1/images/create', formData, { headers: { 'content-type': 'multipart/form-data' } });

      if (res.data == null) {
        return {};
      }

      setImages((images) => images.filter((image) => image.id !== res.data.id).concat({
        id: res.data.id,
        url: res.data.url,
        thumbnail: res.data.thumbnail,
      }));

      return { id: res.data.id, url: res.data.url };
    } catch (error: any) {
      if (error.response && error.response.status === 422 && error.response.data.errors) {
        const validationErrors = error.response.data.errors;
        const firstErrorField = Object.keys(validationErrors)[0];
        const firstErrorMessage = validationErrors[firstErrorField][0]; // Get the first error message of the first field

        setValidationError(firstErrorMessage);
      } else {
        setApiError('予期しないエラーが発生しました。');
      }

      return {};
    }
  }, []);

  const GetImages = useCallback(async (type: ImageType, page?: number, perPage?: number): Promise<ImageItem[]> => {
    setApiError(null); // Resetting the error state at the start of the call

    // Check if the browser perceives it as offline
    if (!navigator.onLine) {
      // "You seem to be offline. Please check your internet connection."
      setApiError('インターネット接続がありません。接続を確認してください。');
      return [];
    }

    const obj = new URLSearchParams();

    obj.append('type', type);

    if (page != null) {
      obj.append('page', `${page}`);
    }

    if (perPage != null) {
      obj.append('per_page', `${perPage}`);
    }

    let url = '/v1/images';
    url += `?${obj.toString()}`;

    try {
      const res = await api.get(url);

      if (res.data == null) {
        return [];
      }
      const result = res.data.data.map((d: ImageItem) => ({
        id: d.id,
        url: d.url,
        thumbnail: d.thumbnail,
      }));
      setImages(result);
      setTotal(res.data.total);
      setLinks(res.data.links);
      setCurrentPage(res.data.current_page);
      setToItem(res.data.to || 0);
      setFromItem(res.data.from || 0);
      return result;
    } catch (error) {
      // Check if error is an instance of Error
      if (error instanceof Error) {
        if (error.message.includes('ERR_INTERNET_DISCONNECTED')) {
          // "You seem to be offline. Please check your internet connection."
          setApiError('インターネット接続がありません。接続を確認してください。');
        } else {
          // "An error occurred while fetching images. Please try again."
          setApiError('画像の取得中にエラーが発生しました。再試行してください。');
        }
        console.error('Error occurred while fetching images:', error);
      } else {
        // Handle any other type of error (this is unlikely to happen with Axios, but it's good to have just in case)
        setApiError('予期しないエラーが発生しました。'); // "An unexpected error occurred."
      }
      return [];
    }
  }, []);

  const destroyImage = useCallback(async (id: number, cb?: () => void) => {
    await api.delete(`/v1/images/${id}/delete`);
    setImages((list) => list.filter((l) => l.id !== id));
    if (cb && typeof cb === 'function') {
      cb();
    }
  }, []);

  const exchangeForUrl = useCallback(async (url: string): Promise<string> => {
    if (!url) {
      return '';
    }
    const result = await api.get('/v1/images/image-stream', { params: { url }, responseType: 'arraybuffer' }).catch((e) => (e.response));
    if (result === undefined) {
      return '';
    }
    if (result.status === 404) {
      return '/img/no-image.jpg';
    }
    if (result.status === 422) {
      return '/img/blocks/sample_block.png';
    }
    const blob = new Blob([result.data], { type: 'image/jpeg' });
    return URL.createObjectURL(blob);
  }, []);

  const reset = useCallback(() => {
    setApiError(null);
    setValidationError(null);
    setImages([]);
    setTotal(0);
    setLinks([]);
    setCurrentPage(1);
    setToItem(0);
    setFromItem(0);
  }, []);

  const value: ImagesContextProps = useMemo<ImagesContextProps>(() => ({
    apiError,
    validationError,
    placeHolderImage,
    Create,
    GetImages,
    images,
    total,
    links,
    currentPage,
    toItem,
    fromItem,
    destroyImage,
    exchangeForUrl,
    reset,
  }), [
    apiError,
    validationError,
    placeHolderImage,
    images,
    Create,
    GetImages,
    total,
    links,
    currentPage,
    toItem,
    fromItem,
    destroyImage,
    exchangeForUrl,
    reset,
  ]);

  return (
    <ImagesContext.Provider value={value}>
      {children}
    </ImagesContext.Provider>
  );
};

export default ImagesProvider;
